import React, { ReactNode, useMemo } from 'react';
import KickImage from 'Reuseables/KickImage';
import { Link } from 'react-router-dom';
import 'antd/es/card/style/css';
import 'antd/es/avatar/style/css';
import 'antd/es/row/style/css';
import 'antd/es/col/style/css';
import Styles from './Card.module.scss';
import { Card as AntdCard, Row, Col } from 'antd';
import { CardMetaProps, CardProps } from 'antd/lib/card';
import Text from '../Text';
import cn from 'classnames';
import Image from '../Image';
import Spacer from '../Spacer';
import {
  formatterForCurrency,
  formatterForCurrencyWithSupText,
} from '../../../Helper/Formatter';
import { LooseObject } from 'helpers/type';
import { joinClassnames } from 'helpers/classNames';
import { IImageProps } from '../Image/Image';

const { Meta } = AntdCard;

type TDivProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

type TDefaultCardProps = CardProps & {
  meta?: React.FC<CardMetaProps>;
};

const DefaultCard: React.FC<TDefaultCardProps> = ({
  meta,
  style,
  children,
  ...props
}) => {
  return (
    <AntdCard style={{ width: 300, ...style }} {...props}>
      {children}
      {meta}
    </AntdCard>
  );
};

export type TCategoryCardProps = {
  title: string;
  description: string;
  width?: number | string;
  height?: number | string;
  backgroundImage?: string;
  onClick?: () => void;
  link?: string;
  useLink?: boolean;
};

const Category: React.FC<TCategoryCardProps> = ({
  title,
  description,
  height = 236,
  onClick = () => {},
  ...props
}) => {
  return (
    <Row
      className={Styles['category-card-container']}
      style={{
        height,
      }}
    >
      <Col
        span={24}
        className={Styles['category-card']}
        style={{
          height,
        }}
        onClick={onClick}
      >
        <Row>
          <Col className={Styles['text-wrapper']}>
            <Text className={Styles['title-text']} level={5}>
              {title}
            </Text>
            <Text className={Styles['description-text']}>{description}</Text>
          </Col>
          <Col className={Styles['products-wrapper']}>{props.children}</Col>
          <Col className={Styles['overlay-hover']}>
            <Text className={Styles['description-text-hover']}>
              {description}
            </Text>
            <Text className={Styles['title-text-hover']}>{title}</Text>
            <Text className={Styles['selection-text-hover']}>Selection</Text>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export type TSubCategoryProps = Omit<TCategoryCardProps, 'description'> & {
  backgroundColor?: string;
  extraBottomComponent?: ReactNode;
};

const SubCategory: React.FC<TSubCategoryProps> = ({
  title,
  width,
  height = 110,
  backgroundImage,
  backgroundColor,
  onClick = () => {},
  extraBottomComponent,
  link,
  useLink,
  ...props
}) => {
  return (
    <div className={Styles['sub-category-card']}>
      <Row align="middle">
        {
          useLink ?
          <Link to={link as string} target="_blank">
            <Col
              span={24}
              style={{
                maxHeight: height,
                backgroundColor,
              }}
              className={Styles['image-wrapper']}
            >
              <Image src={backgroundImage} className={Styles['image']} />
            </Col>
            <Col span={24} className={Styles['text-wrapper']}>
              <Text className={Styles['text']}>
                {title}
              </Text>
              <div className={Styles['extra-bottom-component']}>
                {extraBottomComponent}
              </div>
            </Col>
          </Link>
          :
          <>
            <Col
              span={24}
              style={{
                maxHeight: height,
                backgroundColor,
              }}
              className={Styles['image-wrapper']}
              onClick={onClick}
            >
              <Image src={backgroundImage} className={Styles['image']} />
            </Col>
            <Col span={24} className={Styles['text-wrapper']}>
              <Text className={Styles['text']} onClick={onClick}>
                {title}
              </Text>
              <div className={Styles['extra-bottom-component']}>
                {extraBottomComponent}
              </div>
            </Col>
          </>
        }
      </Row>
    </div>
  );
};

export type TProductCardProps = {
  image: string;
  title: string;
  price: string;
  pricePrefix?: string;
  onClick?: () => void;
  style?: LooseObject;
  lastProduct?: boolean;
  isOnlyKickPoint: boolean;
  userCurrency: any;
};

const Product: React.FC<TProductCardProps> = ({
  image,
  title,
  price,
  pricePrefix = 'IDR',
  onClick = () => {},
  style = {},
  lastProduct,
  isOnlyKickPoint,
  userCurrency
}) => {
  const priceText = useMemo(() => {
    const currency = isOnlyKickPoint? "KP" : userCurrency?.currency?? pricePrefix
    const formattedPrice = formatterForCurrency(price, currency);
    const normalText = formattedPrice.slice(0, -3);
    const supText = formattedPrice.slice(-3);

    return (
      <>
        {normalText}{' '}
        <sup style={{ top: '-0.2em', left: '-0.3em' }}>{supText}</sup>
      </>
    );
  }, [pricePrefix, price]);

  return (
    <div
      className={joinClassnames(
        Styles['product-card'],
        lastProduct && Styles['last-product'],
      )}
      onClick={onClick}
      style={style}
    >
      <Image src={image} className={Styles['product-image']} />
      <Spacer height={23} />
      <Row>
        <Text className={Styles['product-title']}>{title}</Text>
      </Row>
      <Spacer height={4} />
      <Row>
        <Text className={Styles['product-price']}>{priceText}</Text>
      </Row>
    </div>
  );
};

const ProductHorizontal: React.FC<TProductCardProps> = ({
  image,
  title,
  price,
  pricePrefix = 'IDR',
  onClick = () => {},
  isOnlyKickPoint,
  userCurrency
}) => {
  const priceText = useMemo(() => {
    const currency = isOnlyKickPoint? "KP" : userCurrency?.currency?? pricePrefix;
    const formattedPrice = formatterForCurrency(price, currency);
    const { normalText, supText } =
      formatterForCurrencyWithSupText(formattedPrice);

    return (
      <>
        {normalText}{' '}
        <sup style={{ top: '-0.2em', left: '-0.3em' }}>{supText}</sup>
      </>
    );
  }, [pricePrefix, price]);

  return (
    <Row
      className={Styles['product-card-horizontal']}
      onClick={onClick}
      align="middle"
    >
      <Col>
        <Image src={image} className={Styles['image']} />
      </Col>
      <Spacer width={23} />
      <Col className={Styles['wrapper-text']}>
        <Row>
          <Text className={Styles['title']}>{title}</Text>
        </Row>
        <Spacer height={4} />
        <Row>
          <Text className={Styles['price-text']}>{priceText}</Text>
        </Row>
      </Col>
    </Row>
  );
};

const Collection: React.FC<TCategoryCardProps> = ({
  title,
  description,
  backgroundImage = 'https://images4.imagebam.com/c6/67/bc/MEC4V69_o.png',
  width = 286,
  height = 494,
  onClick = () => {},
  ...props
}) => {
  return (
    <Row
      style={{
        width,
        height,
      }}
    >
      <Col
        span={24}
        className={Styles['collection-card']}
        style={{
          width,
          height,
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        <div className={Styles['text-wrapper']}>
          <Text level={5}>{title}</Text>
          <Text className={Styles['description']}>{description}</Text>
        </div>
      </Col>
    </Row>
  );
};

const Social: React.FC<
  IImageProps & Omit<TCategoryCardProps, 'title' | 'description'>
> = ({
  backgroundImage = 'https://cdn.zeplin.io/5c7ca41d3ff280626220444e/assets/851FEC5F-C6D2-4B12-AEE8-D7913708D3C7.png',
  width = 182,
  height = 245,
  onClick = () => {},
  style,
  className,
  ...props
}) => {
  return (
    <Image
      className={cn(Styles['social-card'], className)}
      style={{
        width,
        height,
        ...style,
      }}
      src={backgroundImage}
      onClick={onClick}
      {...props}
    />
  );
};

const Card = Object.assign(DefaultCard, {
  Category,
  Product,
  ProductHorizontal,
  Collection,
  Social,
  SubCategory,
});

export default Card;
